<template>
  <div id="app" :class="{ ios_platform, auth_page: isAuthPage }">
    <native-android-required v-if="requiresNativeAndroid"/>

    <template v-else>
      <loading v-if="load" :fullscrean="true" text="Por favor aguarde"/>
      <div class="error" v-else-if="error">
        <div class="text-center">
          <div class="mb-2"><i class="fa-solid fa-wrench fa-2x"></i></div>
          <p>{{error}}</p>
        </div>
      </div>
      <div v-else>
        <permissao v-if="locationGate && $route.name !== 'PoliticaPrivacidade'" @ready="liberarApp"/>
        <router-view v-else/>
      </div>

      <rastreamento v-if="dados" @location-blocked="bloquearLocalizacao"/>

      <div class="modal-update" v-if="updateAvailable">
        <div>
          <p class="mb-2 mb-3">Uma nova versão está disponível.</p>
          <button class="btn btn-primary" @click="refreshApp()">Atualizar agora</button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
  import Loading from "./components/Loading";
  import Rastreamento from "@/components/Rastreamento";
  import { enableNotificationAudio } from '@/services/notificationAudio';
  import NativeAndroidRequired from "@/views/NativeAndroidRequired";
  import { Capacitor } from '@capacitor/core';
  import Permissao from "@/components/Permissao";
  import { syncStatusBar } from "@/services/statusBar";
  import { applyTheme } from "@/services/theme";

  export default {
    components: {Permissao, NativeAndroidRequired, Rastreamento, Loading},
    data() {
      return {
        load: true,
        token: null,
        dados: false,
        locationGate: false,
        error: false,
        updateAvailable: false,
      }
    },

    computed: {
      needsSoundInteraction() {
        return !this.requiresNativeAndroid && !this.$store.state.soundMuted &&
          !this.$store.state.canPlay;
      },
      isAuthPage() {
        return ['Login', 'Recuperar'].includes(this.$route.name);
      },

      requiresNativeAndroid() {
        return this.$route.name !== 'PoliticaPrivacidade' &&
          process.env.VUE_APP_REQUIRE_NATIVE_ANDROID === 'true' &&
          this.isAndroid &&
          !Capacitor.isNativePlatform();
      }
    },

    methods: {
      removeSoundListeners() {
        document.removeEventListener('click', this.enableSound, true);
        document.removeEventListener('keydown', this.handleSoundKey, true);
      },
      syncSoundListeners() {
        this.removeSoundListeners();
        if (this.needsSoundInteraction) {
          document.addEventListener('click', this.enableSound, true);
          document.addEventListener('keydown', this.handleSoundKey, true);
        }
      },
      handleSoundKey(event) {
        if (event.key === 'Enter' || event.key === ' ') this.enableSound();
      },
      enableSound() {
        if (this.requiresNativeAndroid || this.$store.state.soundMuted || this.$store.state.canPlay) return;
        const enabled = enableNotificationAudio();
        if (enabled) this.removeSoundListeners();
        return enabled;
      },

      autenticar() {
        const appData = localStorage.getItem('appData');

        this.$auth.authenticate(this.token)
          .then(async res => {
            if (res.data.success) {
              await this.iniciarApp(res.data);
              this.load = false;

            } else {
              this.$swal("Não conseguimos autenticar sua empresa. Por favor tente novamente.");
              this.load = false;
            }

          }, res => {
            this.load = false;

            if (!res.status) {
              this.dados = false;
              this.error = false;

              if (this.$route.name !== 'Offline') {
                this.$router.replace('/offline');
              }

            } else if (res.status === 401) {
              this.token = null;
              this.dados = false;
              this.error = false;
              this.locationGate = false;

            } else if (appData) {
              this.iniciarApp(JSON.parse(appData));

            } else {
              this.error = "No momento este canal está indisponível.\nPor favor verifique se sua conexão está estável."
            }
          });
      },

      async iniciarApp(data) {
        if (data.dados.id_funcao !== '5') {
          this.$swal("Este usuário não tem acesso nesta plataforma!").then(() => {
            this.$store.commit('logout');
          });

          return;
        }

        this.$store.commit('setDataUser', data);
        this.empresas = data.bandeiras.length ? data.bandeiras : [{ id_empresa: data.dados.id_empresa, nome_fantasia: data.dados.nome_fantasia, token: data.empresa }];
        this.$store.state.empresas = this.empresas;

        this.bloquearLocalizacao();
      },

      bloquearLocalizacao() {
        this.dados = false;
        this.$store.state.geoLocation = false;
        localStorage.removeItem('geoLocation');
        this.locationGate = true;
      },

      liberarApp() {
        this.locationGate = false;
        localStorage.setItem('geoLocation', 'true');
        this.$store.state.geoLocation = true;
        this.dados = true;

        if (['Login', 'Recuperar', 'Offline', '404'].includes(this.$route.name)) {
          this.$router.replace('/pedidos');
        }

        if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
          this.enableSound();
        }
      },

      setTheme() {
        let theme = localStorage.getItem('theme');

        if (!theme) {
          const isDarkMode = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
          theme = !isDarkMode ? '1' : '2';
        }

        this.$store.state.theme = Number(theme);
        applyTheme(theme);

        syncStatusBar(theme === '1');
      },

      handleBeforeInstallPrompt(event) {
        event.preventDefault();
        this.$store.commit('setPrompt', event);
      },

      handleAppInstalled() {
        this.$store.commit('setPrompt', null);
        this.$store.commit('setPwaInstalled', true);
      },

      refreshApp() {
        this.load = true;
        this.updateAvailable = false;

        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.getRegistrations().then(registrations => {
            for (const reg of registrations) {
              reg.waiting?.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        }

        setTimeout(() => {
          window.location.reload();
        }, 1200);
      }
    },

    watch: {
      needsSoundInteraction() {
        this.syncSoundListeners();
      }
    },

    mounted() {
      this.syncSoundListeners();
      this.setTheme();

      if (this.requiresNativeAndroid) {
        this.load = false;
        return;
      }

      if (this.token) {
        this.autenticar();

      } else {
        this.load = false;

        if (!['Login', 'PoliticaPrivacidade'].includes(this.$route.name)) {
          this.$router.push('/');
        }
      }

      window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt);
      window.addEventListener('appinstalled', this.handleAppInstalled);

      document.addEventListener('swUpdated', () => {
        this.updateAvailable = true;
      });
    },

    created() {
      this.token = this.$route.query.auth || localStorage.getItem('key') || null;
    },

    beforeDestroy() {
      this.removeSoundListeners();
      window.removeEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', this.handleAppInstalled)
    },

    sockets: {
      connect() {
        this.$store.state.online = true;
      },

      disconnect() {
        this.$store.state.online = false;
      },

      connect_error() {
        this.$store.state.online = false;
      }
    }
  }
</script>
<style scoped>
.error {
  height: 85vh;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 15px;
}

.modal-update {
  position: fixed;
  z-index: 9999;
  bottom: 0;
  width: 100%;
  background: var(--bg-theme);
  color: var(--text-default);
  padding: 32px 15px calc(32px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)));
  text-align: center;
  box-shadow: 0 -2px 5px rgba(0, 0, 0, 0.2);
}

@media (min-width: 768px) {
  .modal-update {
    left: 50%;
    width: 430px;
    max-width: 100%;
    transform: translateX(-50%);
    border-radius: 28px 28px 0 0;
  }
}

.dark-mode .modal-update {
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.6); /* sombra mais intensa e difusa */
}


</style>
